/**
 * Back testing: a protocol's scenario types, one per outcome of its graph, with the selected
 * type's decision paths lit in the protocol beside them and its scenarios a step further in.
 * The protocol column can be hidden, so the types get the room. With none open, the protocols to
 * pick from fill the page.
 */

import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { ArrowLeft, FileText, Loader2, PanelLeftClose } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { useAllSOPs } from '@/hooks';
import { sopTitle } from '@/modules/back-testing/format';
import { ProtocolPicker } from '@/modules/back-testing/sop/protocol-picker';
import { SopState } from '@/modules/back-testing/sop/sop-state';
import { SopScenariosPanel } from '@/modules/back-testing/scenarios/sop-scenarios-panel';
import { CandidateTicketsSheet, type TicketSheetSource } from '@/modules/back-testing/sop/candidate-tickets-sheet';
import type { SOP } from '@/types';

// The protocol's own editor and graph viewer ride with it, and neither belongs in the page chunk.
const ProtocolPanel = lazy(() => import('@/modules/back-testing/sop/protocol-panel'));

/** The open protocol's document or graph; hidden, the column slides out by its own width. */
const COLUMN = { width: 'w-[560px]', hidden: '-ml-[560px]' };

export default function BackTesting() {
	const [searchParams, setSearchParams] = useSearchParams();
	const selectedId = searchParams.get('sop') ?? '';
	// Picking cases for a dataset outlives moving between protocols; only the dataset page ends it.
	const pickingFor = searchParams.get('dataset');
	const keepPicking = (params: Record<string, string>): Record<string, string> =>
		pickingFor ? { ...params, dataset: pickingFor } : params;
	const [sopOpen, setSopOpen] = useState(true);
	const [ticketsSop, setTicketsSop] = useState<SOP | null>(null);
	const [ticketsSource, setTicketsSource] = useState<TicketSheetSource>('mapped');

	const { data: sops = [], isLoading: sopsLoading, isError: sopsFailed } = useAllSOPs();

	const selected = useMemo(() => sops.find((s) => s.id === selectedId) ?? null, [sops, selectedId]);
	// Back testing reads protocols: the picker lists the SOPs that hold a protocol's text.
	const protocolSops = useMemo(() => sops.filter((s) => Boolean(s.workflowId)), [sops]);
	const protocolId = selected?.workflowId ?? null;

	/**
	 * Reading conversations is not reading the SOP, and three columns leave the
	 * picker little room, so opening the past scenarios folds the SOP away and
	 * coming back out unfolds it. Only when this did the folding: a reader who
	 * hid or showed it themselves has said what they want, and gets to keep it.
	 */
	const viewingScenarios = searchParams.get('view') === 'scenarios' || Boolean(searchParams.get('scenario'));
	const autoHidRef = useRef(false);
	const sopOpenRef = useRef(sopOpen);
	useEffect(() => {
		sopOpenRef.current = sopOpen;
	}, [sopOpen]);
	useEffect(() => {
		if (viewingScenarios) {
			if (sopOpenRef.current) {
				autoHidRef.current = true;
				setSopOpen(false);
			}
		} else if (autoHidRef.current) {
			autoHidRef.current = false;
			setSopOpen(true);
		}
	}, [viewingScenarios]);
	const toggleSop = useCallback(() => {
		autoHidRef.current = false;
		setSopOpen((v) => !v);
	}, []);
	// The URL names an SOP the list has not delivered yet: a loading state, not "pick one".
	const resolving = Boolean(selectedId) && !selected && sopsLoading;

	const viewTickets = (sop: SOP, source: TicketSheetSource) => {
		setTicketsSource(source);
		setTicketsSop(sop);
	};
	const ticketsSheet = (
		<CandidateTicketsSheet sop={ticketsSop} source={ticketsSource} onOpenChange={(open) => !open && setTicketsSop(null)} />
	);

	if (!selected && !resolving) {
		return (
			<>
				<ProtocolPicker
					sops={protocolSops}
					loading={sopsLoading}
					failed={sopsFailed}
					onSelect={(sop) => setSearchParams(keepPicking({ sop: sop.id }))}
					onViewTickets={viewTickets}
				/>
				{ticketsSheet}
			</>
		);
	}

	return (
		<div className='flex h-svh flex-col gap-2 p-3'>
			{/* Which SOP is open, and the way back out, belong here rather than in the
			    SOP column: hiding that column must not take the only exit with it. */}
			<div className='flex items-center justify-between gap-3 border-b pb-2'>
				{selected ? (
					<div className='flex min-w-0 items-center gap-2'>
						<Button
							variant='ghost'
							size='icon'
							className='size-7 shrink-0'
							onClick={() => setSearchParams(keepPicking({}))}
							aria-label='Back to protocols'
						>
							<ArrowLeft className='size-4' />
						</Button>
						<h1 className='truncate text-base font-semibold capitalize'>{sopTitle(selected.intent)}</h1>
						<SopState sop={selected} />
					</div>
				) : (
					<Skeleton className='h-5 w-56' />
				)}
				{selected && (
					<Button variant='outline' size='sm' className='shrink-0 gap-1.5 text-xs' onClick={toggleSop}>
						{sopOpen ? <PanelLeftClose className='size-3.5' /> : <FileText className='size-3.5' />}
						{sopOpen ? 'Hide protocol' : 'Show protocol'}
					</Button>
				)}
			</div>

			<div className='flex min-h-0 flex-1 overflow-hidden'>
				{/* Kept mounted and slid out on its margin, so hiding it animates: as a flex item the
				    column would not collapse below its own content, so animating width does nothing.
				    The row clips what slides past its edge. */}
				<aside
					className={cn(
						'flex shrink-0 flex-col overflow-hidden border-r transition-[margin-left] duration-300 ease-out',
						COLUMN.width,
						!sopOpen && COLUMN.hidden
					)}
					aria-hidden={!sopOpen}
				>
					<div className={cn('flex h-full flex-col', COLUMN.width)}>
						{protocolId ? (
							<Suspense fallback={<Skeleton className='m-3 h-24' />}>
								<ProtocolPanel protocolId={protocolId} />
							</Suspense>
						) : selected ? (
							<p className='p-3 text-xs text-muted-foreground'>
								This SOP is not the text of a protocol, so there is no path to light. Back testing reads protocols.
							</p>
						) : (
							<div className='flex flex-col gap-2 p-3'>
								<Skeleton className='h-4 w-3/4' />
								<Skeleton className='h-4 w-full' />
								<Skeleton className='h-4 w-5/6' />
							</div>
						)}
					</div>
				</aside>

				<main className='min-w-0 flex-1 overflow-hidden'>
					{selected ? (
						<div className='flex h-full min-h-0 flex-col p-3'>
							<SopScenariosPanel
								sopId={selected.id}
								subcategory={selected.subcategory}
								onViewTickets={(source) => viewTickets(selected, source)}
							/>
						</div>
					) : (
						<div className='flex h-full items-center justify-center'>
							<Loader2 className='size-5 animate-spin text-muted-foreground' />
						</div>
					)}
				</main>
			</div>
			{ticketsSheet}
		</div>
	);
}
